// src/utils/dateUtils.js

const DEFAULT_INVALID_DATE = 'Invalid date';
const DEFAULT_JUST_NOW = 'Just now';
const DEFAULT_IN_FUTURE = 'In the future';
const DEFAULT_YESTERDAY = 'Yesterday';

const translate = (t, key, options = {}, fallback = key) => {
  if (typeof t === 'function') {
    return t(key, { ...options, defaultValue: fallback });
  }
  return fallback;
};

const toTimestampMillis = (value) => {
  if (!value || typeof value !== 'object') return null;

  const seconds =
    typeof value.seconds === 'number'
      ? value.seconds
      : typeof value._seconds === 'number'
        ? value._seconds
        : null;
  if (!Number.isFinite(seconds)) {
    return null;
  }

  const nanoseconds =
    typeof value.nanoseconds === 'number'
      ? value.nanoseconds
      : typeof value._nanoseconds === 'number'
        ? value._nanoseconds
        : 0;
  const safeNanoseconds = Number.isFinite(nanoseconds) ? nanoseconds : 0;

  return seconds * 1000 + Math.floor(safeNanoseconds / 1e6);
};

/**
 * Normalize a date-like input to a valid Date instance.
 * Supports JS Date, Firestore Timestamp-like objects, ISO strings, and epoch numbers.
 * @param {Date|Object|string|number} value
 * @returns {Date|null}
 */
export const coerceDateValue = (value) => {
  if (value == null) return null;

  if (value instanceof Date) {
    return Number.isFinite(value.getTime()) ? value : null;
  }

  if (typeof value?.toDate === 'function') {
    try {
      const converted = value.toDate();
      return Number.isFinite(converted?.getTime()) ? converted : null;
    } catch {
      return null;
    }
  }

  if (typeof value?.toMillis === 'function') {
    try {
      const millis = value.toMillis();
      const converted = new Date(millis);
      return Number.isFinite(converted.getTime()) ? converted : null;
    } catch {
      return null;
    }
  }

  const timestampMillis = toTimestampMillis(value);
  if (timestampMillis !== null) {
    const converted = new Date(timestampMillis);
    return Number.isFinite(converted.getTime()) ? converted : null;
  }

  if (typeof value === 'string' || typeof value === 'number') {
    const converted = new Date(value);
    return Number.isFinite(converted.getTime()) ? converted : null;
  }

  return null;
};

export const resolveLocale = (locale) => {
  if (typeof locale !== 'string') return undefined;
  const trimmed = locale.trim();
  return trimmed || undefined;
};

export const resolveTimeZone = (timeZone) => {
  if (typeof timeZone !== 'string') return null;
  const trimmed = timeZone.trim();
  if (!trimmed) return null;

  try {
    Intl.DateTimeFormat(undefined, { timeZone: trimmed });
    return trimmed;
  } catch {
    return null;
  }
};

export const buildDateKey = (date, { locale, timeZone } = {}) => {
  const safeDate = coerceDateValue(date);
  if (!safeDate) return '';
  const safeTimeZone = resolveTimeZone(timeZone);

  const parts = new Intl.DateTimeFormat(resolveLocale(locale), {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    numberingSystem: 'latn',
    ...(safeTimeZone ? { timeZone: safeTimeZone } : {}),
  }).formatToParts(safeDate);

  const values = {};
  for (const part of parts) {
    if (part.type !== 'literal') {
      values[part.type] = part.value;
    }
  }

  if (!values.year || !values.month || !values.day) {
    return '';
  }

  return `${values.year}-${values.month}-${values.day}`;
};

export const isSameCalendarDay = (left, right, { locale, timeZone } = {}) => {
  const leftDate = coerceDateValue(left);
  const rightDate = coerceDateValue(right);
  if (!leftDate || !rightDate) return false;
  return (
    buildDateKey(leftDate, { locale, timeZone }) ===
    buildDateKey(rightDate, { locale, timeZone })
  );
};

/**
 * Format a date to a locale-aware readable string.
 * @param {Date|Object|string|number} date
 * @param {Object} options
 * @param {string} options.locale
 * @param {string} options.timeZone
 * @param {Intl.DateTimeFormatOptions} options.formatOptions
 * @param {Function} options.t
 * @returns {string}
 */
export const formatDate = (
  date,
  {
    locale,
    timeZone,
    formatOptions = {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    },
    t,
  } = {}
) => {
  const safeDate = coerceDateValue(date);
  if (!safeDate) {
    return translate(t, 'common.invalid_date', {}, DEFAULT_INVALID_DATE);
  }

  const safeTimeZone = resolveTimeZone(timeZone);
  return new Intl.DateTimeFormat(resolveLocale(locale), {
    ...formatOptions,
    ...(safeTimeZone ? { timeZone: safeTimeZone } : {}),
  }).format(safeDate);
};

/**
 * Format relative time in a long, translatable style (e.g., "2 days ago").
 * @param {Date|Object|string|number} date
 * @param {Object} options
 * @param {string} options.locale
 * @param {Date|Object|string|number} options.now
 * @param {Function} options.t
 * @returns {string}
 */
export const formatTimeAgo = (date, { locale, now = new Date(), t } = {}) => {
  const inputDate = coerceDateValue(date);
  const localeCode = resolveLocale(locale);
  const localeOptions = localeCode ? { lng: localeCode } : {};
  if (!inputDate) {
    return translate(t, 'common.invalid_date', localeOptions, DEFAULT_INVALID_DATE);
  }

  const nowDate = coerceDateValue(now) || new Date();
  const diffInSeconds = Math.floor((nowDate.getTime() - inputDate.getTime()) / 1000);

  if (diffInSeconds < 0) {
    return translate(t, 'records.time.relative.in_future', localeOptions, DEFAULT_IN_FUTURE);
  }

  if (diffInSeconds < 60) {
    return translate(t, 'records.time.relative.just_now', localeOptions, DEFAULT_JUST_NOW);
  }

  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) {
    return translate(
      t,
      'records.time.relative.minute_ago',
      { ...localeOptions, count: diffInMinutes },
      diffInMinutes === 1 ? '1 minute ago' : `${diffInMinutes} minutes ago`
    );
  }

  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) {
    return translate(
      t,
      'records.time.relative.hour_ago',
      { ...localeOptions, count: diffInHours },
      diffInHours === 1 ? '1 hour ago' : `${diffInHours} hours ago`
    );
  }

  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays === 1) {
    return translate(t, 'records.time.relative.yesterday', localeOptions, DEFAULT_YESTERDAY);
  }

  if (diffInDays < 7) {
    return translate(
      t,
      'records.time.relative.day_ago',
      { ...localeOptions, count: diffInDays },
      diffInDays === 1 ? '1 day ago' : `${diffInDays} days ago`
    );
  }

  if (diffInDays < 30) {
    const weeks = Math.floor(diffInDays / 7);
    return translate(
      t,
      'records.time.relative.week_ago',
      { ...localeOptions, count: weeks },
      weeks === 1 ? '1 week ago' : `${weeks} weeks ago`
    );
  }

  if (diffInDays < 365) {
    const months = Math.floor(diffInDays / 30);
    return translate(
      t,
      'records.time.relative.month_ago',
      { ...localeOptions, count: months },
      months === 1 ? '1 month ago' : `${months} months ago`
    );
  }

  const years = Math.floor(diffInDays / 365);
  return translate(
    t,
    'records.time.relative.year_ago',
    { ...localeOptions, count: years },
    years === 1 ? '1 year ago' : `${years} years ago`
  );
};

/**
 * Format relative time in compact style (e.g., "5m", "2h", "3d").
 * Falls back to short month/day date for older values.
 * @param {Date|Object|string|number} date
 * @param {Object} options
 * @param {string} options.locale
 * @param {Date|Object|string|number} options.now
 * @param {string} options.timeZone
 * @param {Function} options.t
 * @returns {string}
 */
export const formatTimeAgoCompact = (
  date,
  { locale, now = new Date(), timeZone, t } = {}
) => {
  const inputDate = coerceDateValue(date);
  const localeCode = resolveLocale(locale);
  const localeOptions = localeCode ? { lng: localeCode } : {};
  if (!inputDate) {
    return translate(t, 'records.time.relative.just_now', localeOptions, DEFAULT_JUST_NOW);
  }

  const nowDate = coerceDateValue(now) || new Date();
  const diffInSeconds = Math.floor((nowDate.getTime() - inputDate.getTime()) / 1000);

  if (diffInSeconds < 0 || diffInSeconds < 60) {
    return translate(t, 'records.time.relative.just_now', localeOptions, DEFAULT_JUST_NOW);
  }

  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) {
    return translate(
      t,
      'records.time.relative.short.minute_ago',
      { ...localeOptions, count: diffInMinutes },
      `${diffInMinutes}m`
    );
  }

  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) {
    return translate(
      t,
      'records.time.relative.short.hour_ago',
      { ...localeOptions, count: diffInHours },
      `${diffInHours}h`
    );
  }

  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 7) {
    return translate(
      t,
      'records.time.relative.short.day_ago',
      { ...localeOptions, count: diffInDays },
      `${diffInDays}d`
    );
  }

  return formatDate(inputDate, {
    locale,
    timeZone,
    t,
    formatOptions: {
      month: 'short',
      day: 'numeric',
    },
  });
};
